import { create } from 'zustand';

const statusTypes = {
  IDLE: 'idle',
  POLLING: 'polling',
  SUCCESS: 'success',
  ERROR: 'error',
};

/**
 * Polling Store for Session Status Management
 * 
 * PURPOSE:
 * Manages the state of session polling operations and ensures that only ONE polling
 * chain exists per sessionId, regardless of how many components try to start polling.
 * 
 * WHY THIS IS NEEDED:
 * - Multiple RecordsChecker components might be mounted with the same sessionId
 * - Without deduplication, multiple polling chains would create unnecessary API calls
 * - Race conditions could occur between concurrent polling operations
 * - Server resources would be wasted with duplicate polling requests
 * 
 * HOW IT WORKS:
 * 1. activeSessionIds Set tracks which sessions are currently being polled
 * 2. startPolling(sessionId) checks if already polling, returns false if so
 * 3. Components that get false from startPolling exit early without starting polls
 * 4. Success/error/stop methods automatically clean up the activeSessionIds Set
 * 
 * USAGE PATTERN:
 * ```js
 * const { startPolling, stopPolling, setSuccess, setError } = usePollingStore();
 * 
 * // In component:
 * const shouldPoll = startPolling(sessionId);
 * if (!shouldPoll) return; // Already polling elsewhere
 * 
 * // Start your polling logic...
 * // On success: setSuccess(data, sessionId) - auto-removes from active set
 * // On error: setError(error, sessionId) - auto-removes from active set
 * // On unmount: stopPolling(sessionId) - manual cleanup
 * ```
 */
export const usePollingStore = create((set, get) => ({
  data: null,
  error: null,
  status: statusTypes.IDLE,
  activeSessionIds: new Set(), // Track which sessionIds are currently being polled

  /**
   * Attempts to start polling for a specific sessionId
   * @param {string} sessionId - The session to start polling for
   * @returns {boolean} true if polling started, false if already polling
   */
  startPolling: (sessionId) => {
    const state = get();
    if (state.activeSessionIds.has(sessionId)) {
      return false; // Polling already active for this sessionId
    }

    const newActiveSessionIds = new Set(state.activeSessionIds);
    newActiveSessionIds.add(sessionId);

    set({
      data: null,
      error: null,
      status: statusTypes.POLLING,
      activeSessionIds: newActiveSessionIds,
    });
    return true; // Polling started
  },

  /**
   * Manually stops polling for a specific sessionId
   * @param {string} sessionId - The session to stop polling for
   */
  stopPolling: (sessionId) => {
    const state = get();
    const newActiveSessionIds = new Set(state.activeSessionIds);
    newActiveSessionIds.delete(sessionId);

    set({
      activeSessionIds: newActiveSessionIds,
    });
  },

  /**
   * Sets success state and automatically removes sessionId from active polling
   * @param {any} data - The successful response data
   * @param {string} sessionId - The session that completed successfully (optional)
   */
  setSuccess: (data, sessionId) => {
    const state = get();
    const newActiveSessionIds = new Set(state.activeSessionIds);
    if (sessionId) {
      newActiveSessionIds.delete(sessionId);
    }

    set({
      data,
      status: statusTypes.SUCCESS,
      activeSessionIds: newActiveSessionIds,
    })
  },

  /**
   * Sets error state and automatically removes sessionId from active polling
   * @param {Error} error - The error that occurred
   * @param {string} sessionId - The session that failed (optional)
   */
  setError: (error, sessionId) => {
    console.error('Polling store error', error);
    const state = get();
    const newActiveSessionIds = new Set(state.activeSessionIds);
    if (sessionId) {
      newActiveSessionIds.delete(sessionId);
    }

    set({
      error,
      status: statusTypes.ERROR,
      activeSessionIds: newActiveSessionIds,
    })
  },

  /**
   * Resets the store to initial state (does NOT clear activeSessionIds)
   * Use this to reset UI state without affecting active polling operations
   */
  reset: () => {
    set({
      data: null,
      error: null,
      status: statusTypes.IDLE,
    })
  }
}));

export { statusTypes };
